import React, { useState } from "react";
import "../../../../styles/AuditReport.css";
import AttachmentCard from "../../../../components/common/AttachmentCard";
import { useAttachmentPreview } from "../../../../utils/hooks/useAttachmentPreview";
import AttachmentPreviewModal from "../../../../components/modals/AttachmentPreviewModal";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import useScoreScale, {getScoreConfig} from "../../../../utils/hooks/useScoreScale1";
import { Spin } from "antd";

const Section = ({
  section,
  ansData,
  RepSecData,
  idx,
  attachments,
  isPrinting,
  colorScale,
}) => {
  const sectionData = RepSecData.find((data) => data.section === section.id);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [isOpen, setIsOpen] = useState(true);

  const { totalObtainedMarks, totalMaxMarks } = section.questions.reduce(
    (acc, question) => {
      const answer = ansData.find((ans) => ans.question === question.id);
      if (
        answer &&
        answer.answer_text &&
        answer.answer_text.trim().toUpperCase() !== "NA"
      ) {
        acc.totalObtainedMarks += answer.marks_obtained || 0;
        acc.totalMaxMarks += question.max_marks;
      }
      return acc;
    },
    { totalObtainedMarks: 0, totalMaxMarks: 0 },
  );

  const sectionMarks = RepSecData.find((item) => item.section === section.id);

  const sectionPercentage =
    sectionMarks?.not_applicable || totalMaxMarks === 0
      ? 0
      : (totalObtainedMarks / totalMaxMarks) * 100;

  const sectionScoreConfig = useScoreScale(sectionPercentage, colorScale);
  const sectionStyles = sectionMarks?.not_applicable
    ? {
        bgColor: "rgb(242,242,242)",
        color: "#000",
      }
    : {
        bgColor: sectionScoreConfig.bgColor,
        color: sectionScoreConfig.color,
      };

  const renderFormattedText = (text = "") => {
    if (!text.includes("**")) return text;

    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const { getPreviewConfig, getModalPreviewSrc } =
    useAttachmentPreview(attachments);

  return (
    <div className="section-table mt-2">
      <div
        className="sec-name section-collapse-header"
        style={{
          backgroundColor: sectionStyles.bgColor,
          color: sectionStyles.color,
          borderBottom: `1px solid ${sectionStyles.bgColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <p className="ms-3 my-3">
          {idx + 1}. {section.name}
        </p>
        <span className="px-3">
          {isOpen ? <DownOutlined /> : <UpOutlined />}
        </span>
      </div>

      <div
        style={{ overflowX: "auto" }}
        className={`${isOpen ? "" : "d-none"}`}
      >
        <table>
          <thead>
            <tr>
              <th style={{ width: "6vw" }}>#</th>
              <th className="text-start" style={{ width: "35vw" }}>
                Question
              </th>
              <th
                className="text-start"
                style={{ width: "35vw", textAlign: "start !important" }}
              >
                Answer
              </th>
              <th style={{ width: "10vw" }}>Marks</th>
              <th style={{ width: "10vw" }}>Max. Marks</th>
            </tr>
          </thead>
          <tbody>
            {section.questions.map((question, index) => {
              const answer = ansData.find(
                (ans) => ans.question === question.id,
              );
              if (!answer) {
                return (
                  <tr key={question.id}>
                    <td>{question.sequence}</td>
                    <td className="text-start">
                      {renderFormattedText(question.question_txt)}
                    </td>
                    <td className="text-start">No answer provided</td>
                    <td>0</td>
                    <td>{question.max_marks}</td>
                  </tr>
                );
              }
              const isNotApplicable = answer.not_applicable === true;
              let marksBackgroundColor = "";

              if (!isNotApplicable && question.max_marks > 0) {
                const percentage =
                  (Number(answer.marks_obtained || 0) /
                    Number(question.max_marks)) *
                  100;

                const scoreData = getScoreConfig(percentage, colorScale);
                marksBackgroundColor = scoreData.bgColor;
              }

              return (
                <tr key={question.id}>
                  <td>{question.sequence}</td>
                  <td className="text-start">
                    {renderFormattedText(question.question_txt)}
                  </td>
                  <td className="text-start">
                    {isNotApplicable ? (
                      "Not Applicable"
                    ) : (
                      <>
                        {answer.answer_text || "No answer provided"}
                        {answer.answer_comment &&
                          answer.answer_comment.trim() !== "" && (
                            <span style={{ color: "#888" }}>
                              {" "}
                              ({answer.answer_comment})
                            </span>
                          )}
                      </>
                    )}
                  </td>

                  {isNotApplicable ? (
                    <>
                      <td colSpan={2} className="text-center text-muted">
                        --
                      </td>
                    </>
                  ) : (
                    <>
                      <td
                        style={{
                          backgroundColor: marksBackgroundColor,
                        }}
                      >
                        {answer.marks_obtained || 0}
                      </td>
                      <td>{question.max_marks}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {sectionData && (
          <div
            className="section-summary mt-4 p-3"
            style={{ background: "#f5f5f5" }}
          >
            {sectionMarks && !sectionMarks.not_applicable && (
              <p style={{ fontWeight: "bold" }}>
                Total Marks: {sectionMarks.marks_obtained} out of{" "}
                {sectionMarks.max_marks}
              </p>
            )}

            {!isPrinting && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  width: "100%",
                }}
              >
                {attachments.length ? (
                  attachments.map((att) => (
                    <AttachmentCard
                      key={att.id}
                      attachment={att}
                      previewConfig={getPreviewConfig(att)}
                      onPreview={setPreviewAttachment}
                    />
                  ))
                ) : (
                  <p>No attachments available for this section.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* =============================== Modal =============================== */}
      {previewAttachment && (
        <AttachmentPreviewModal
          open={!!previewAttachment}
          attachment={{
            ...previewAttachment,
            direct_url: getModalPreviewSrc(previewAttachment),
          }}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );
};

const ARQuestionnaire = ({
  isPrinting,
  ansData,
  RepSecData,
  sectionsWithAttachments,
  primaryColor,
  colorScale,
}) => {
  const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(true);

  return (
    <div className="card m-0">
      <div
        className="card-header"
        onClick={() => setIsQuestionnaireOpen((prev) => !prev)}
        style={{
          backgroundColor: primaryColor || undefined,
          color: primaryColor ? "#fff" : undefined,
        }}
      >
        <p className="m-0">Questionnaire</p>
        {!isQuestionnaireOpen ? <UpOutlined /> : <DownOutlined />}
      </div>
      <div className={`${isQuestionnaireOpen ? "" : "d-none"} `}>
        {sectionsWithAttachments && sectionsWithAttachments.length > 0 ? (
          sectionsWithAttachments.map((section, idx) => (
            <div key={section.id} className="my-4 mx-2">
              <Section
                section={section}
                ansData={ansData}
                idx={idx}
                RepSecData={RepSecData}
                attachments={section.attachments}
                isPrinting={isPrinting}
                colorScale={colorScale}
              />
            </div>
          ))
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="small" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ARQuestionnaire;
